import React from 'react';
import { Group, Rect, Text } from 'react-konva';
import { FONT_FAMILY } from '../../constants/typography';
import type { KonvaMouseEvent } from '../../types';
import { measureLabelSize, measureWrappedText } from '../../utils/measureLabel';

interface HaloTextProps {
  x: number;
  y: number;
  width?: number;
  height?: number;
  text: string;
  fontSize: number;
  fontFamily?: string;
  fontStyle?: string;
  fill: string;
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  wrap?: 'word' | 'none' | 'char';
  ellipsis?: boolean;
  haloFill: string;
  padX?: number;
  padY?: number;
  /** Pixel line height used for measurement (Konva uses fontSize * lineHeight). */
  lineHeightPx?: number;
  onDblClick?: (e: KonvaMouseEvent) => void;
  listening?: boolean;
}

/** Soft wash behind glyphs — translucent, no chip border or shadow. */
export const LABEL_HALO_ALPHA = 0.3;

/** Text with a rounded alpha plate so glyphs stay readable over icons, lines, and the grid. */
export const HaloText: React.FC<HaloTextProps> = ({
  x,
  y,
  width,
  height,
  text,
  fontSize,
  fontFamily = FONT_FAMILY,
  fontStyle,
  fill,
  align = 'left',
  verticalAlign = 'top',
  wrap,
  ellipsis,
  haloFill,
  padX = 4,
  padY = 2,
  lineHeightPx,
  onDblClick,
  listening = true,
}) => {
  if (!text) return null;
  const linePx = lineHeightPx ?? fontSize;
  const font = `${fontStyle ? `${fontStyle} ` : ''}${fontSize}px ${fontFamily}`;
  const measured = wrap === 'word' && width != null
    ? measureWrappedText(text, width, font, linePx)
    : measureLabelSize(text, font, linePx);
  const boxW = Math.min(width ?? Infinity, measured.width) + padX * 2;
  const boxH = measured.height + padY * 2;
  const layoutW = width ?? boxW;
  const layoutH = height ?? boxH;
  let hx = x;
  if (align === 'center') hx = x + (layoutW - boxW) / 2;
  else if (align === 'right') hx = x + layoutW - boxW;
  let hy = y;
  if (verticalAlign === 'middle') hy = y + (layoutH - boxH) / 2;
  else if (verticalAlign === 'bottom') hy = y + layoutH - boxH;

  return (
    <Group>
      <Rect
        x={hx}
        y={hy}
        width={Math.max(boxW, padX * 2 + 4)}
        height={Math.max(boxH, padY * 2 + 4)}
        fill={haloFill}
        cornerRadius={4}
        listening={false}
        perfectDrawEnabled={false}
      />
      <Text
        x={x}
        y={y}
        width={width}
        height={height}
        text={text}
        fontSize={fontSize}
        fontFamily={fontFamily}
        fontStyle={fontStyle}
        fill={fill}
        align={align}
        verticalAlign={verticalAlign}
        wrap={wrap}
        ellipsis={ellipsis}
        lineHeight={linePx / fontSize}
        onDblClick={onDblClick}
        listening={listening}
        perfectDrawEnabled={false}
      />
    </Group>
  );
};
